import {
  ApiError,
  CancelablePromise,
  type ListVaultCategoriesResponse,
  type VaultAllCategoriesInfo,
  type VaultCategory,
  VaultService,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
} from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import { closeVault } from '@/models/vault/vault.ts'
import { handleError } from '@/utils/error'
import { debounce } from '@/utils/helpers.ts'

export const renameVaultCategoryFx = attach({
  name: 'rename vault category',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    params: {
      categoryId: number
      name: string
    },
    { creatorId },
  ) => ({
    ...params,
    creatorId: creatorId!,
  }),

  effect: createEffect<
    {
      creatorId: number
      categoryId: number
      name: string
    },
    VaultCategory,
    ApiError
  >(async ({ categoryId, creatorId, name }) => {
    const response = await VaultService.renameCategory({
      categoryId,
      creatorId,
      requestBody: { name },
    })

    if (response.error) {
      // @ts-ignore
      throw new ApiError({}, { body: { detail: response.error.message } })
    }

    return response as VaultCategory
  }),
})

renameVaultCategoryFx.doneData.watch(() => {
  toast.success('Category has been renamed successfully')
})

renameVaultCategoryFx.failData.watch((error) => {
  handleError(error, 'Unexpected error occurred, please try again later')
})

export const deleteVaultCategoryFx = attach({
  name: 'delete vault category',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    params: {
      categoryId: number
      clearMedia?: boolean
    },
    { creatorId },
  ) => ({
    ...params,
    creatorId: creatorId!,
  }),

  effect: createEffect<
    {
      creatorId: number
      categoryId: number
      clearMedia?: boolean
    },
    number,
    ApiError
  >(async ({ categoryId, creatorId, clearMedia = false }) => {
    await VaultService.deleteCategory({
      categoryId,
      creatorId,
      clearMedia,
    })

    return categoryId
  }),
})

deleteVaultCategoryFx.doneData.watch(() => {
  toast.success('Category has been deleted successfully')
})

deleteVaultCategoryFx.failData.watch((error) => {
  handleError(error, 'Error deleting vault category')
})

export const createVaultCategoryFx = attach({
  name: 'create vault category',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (name: string, { creatorId }) => ({
    name,
    creatorId: creatorId!,
  }),

  effect: createEffect<
    {
      name: string
      creatorId: number
    },
    VaultCategory,
    ApiError
  >(async ({ name, creatorId }) => {
    const response = await VaultService.createCategory({
      creatorId,
      // @ts-ignore Bad swagger types, need to fix
      requestBody: { name },
    })

    if (response.error) {
      throw new Error(response.error.message)
    }

    return response as VaultCategory
  }),
})

createVaultCategoryFx.doneData.watch(() => {
  toast.success('Category has been added successfully')
})

createVaultCategoryFx.failData.watch((error) => {
  handleError(
    error,
    'Something went wrong while creating category. Try again later!',
  )
})

export const setSelectedCategory = createEvent<Nullable<VaultCategory>>(
  'set vault selected category',
)
export const $selectedCategory = createStore<Nullable<VaultCategory>>(null, {
  name: 'vault selected category',
})
  .on(
    [setSelectedCategory, renameVaultCategoryFx.doneData],
    (_, category) => category,
  )
  .reset(deleteVaultCategoryFx.doneData)

export const $showMediaItems = $selectedCategory.map((category) => !!category)

export const setCategoryQueryParam = createEvent<string>(
  "set vault selected category's query",
)
export const setCategoryQuery = debounce(setCategoryQueryParam, 500)
export const $categoryQueryParam = createStore('', {
  name: "vault selected category's query",
}).on(setCategoryQueryParam, (_, query) => query)

let prevRequest: Nullable<CancelablePromise<any>> = null

export const getVaultCategoriesFx = attach({
  name: 'get vault categories by page',
  source: {
    creatorId: $creatorId,
    query: $categoryQueryParam,
  },
  mapParams: (offset: number, { creatorId, query }) => ({
    offset,
    creatorId: creatorId!,
    query,
  }),
  effect: createEffect<
    {
      creatorId: number
      offset: number
      query: string
    },
    ListVaultCategoriesResponse,
    ApiError
  >(async ({ offset, creatorId, query }) => {
    // `cancel` could not exist in unit tests
    prevRequest?.cancel?.()
    prevRequest = VaultService.listCategories({
      creatorId,
      requestBody: { query, offset, limit: 10, view: 'main' },
    })

    return prevRequest
  }),
})

getVaultCategoriesFx.fail.watch(({ error, params }) => {
  handleError(
    error,
    'Something went wrong while getting vault categories',
    () => getVaultCategoriesFx(params),
  )
})

export const getVaultCategoryFx = attach({
  name: 'get vault ONE category by id',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (categoryId: number, { creatorId }) => ({
    categoryId,
    creatorId: creatorId!,
  }),
  effect: createEffect(VaultService.getCategory),
})

export const $vaultCategoriesError = createStore<Nullable<Error>>(null)
  .on(getVaultCategoriesFx.failData, (_, error) => error)
  .reset(closeVault, getVaultCategoriesFx)

export const refetchVaultCategories = createEvent('refetch vault categories')
export const $vaultCategoriesPages = createStore<ListVaultCategoriesResponse[]>(
  [],
)
  .on(getVaultCategoriesFx.doneData, (state, data) => [...state, data])
  .reset(refetchVaultCategories)

export const $allCategoryDetails = createStore<
  Nullable<VaultAllCategoriesInfo>
>(null)
  .on($vaultCategoriesPages, (_, pages) => pages?.[0]?.all)
  .on(getVaultCategoryFx.done, (state, { params: categoryId, result }) =>
    categoryId ? state : result,
  )

export const $canLoadMoreVaultCategoryPages = combine(
  $vaultCategoriesPages,
  getVaultCategoriesFx.pending,
  (pages, isLoading) =>
    (pages.length ? pages[pages.length - 1].hasMore : true) && !isLoading,
)

export const getNextVaultCategoriesPageFx = attach({
  name: "get vault categories' next page",
  source: { pages: $vaultCategoriesPages },
  mapParams: (_: void, { pages }) => pages,
  effect: createEffect({
    handler: async (pages: ListVaultCategoriesResponse[]) => {
      const offset = pages.reduce((result, { list }) => result + list.length, 0)

      return getVaultCategoriesFx(offset)
    },
  }),
})

export const updateVaultCategory = createEvent<VaultCategory>(
  'update vault category',
)
export const $vaultCategories = createStore<VaultCategory[]>([])
  .on(getVaultCategoriesFx.doneData, (state, { list }) => {
    return state.concat(list)
  })
  .on(deleteVaultCategoryFx.doneData, (state, id) => {
    return state.filter((category) => category.id !== id)
  })
  .on(renameVaultCategoryFx.doneData, (state, { id, name }) => {
    return state.map((c) => (c.id === id ? { ...c, name } : c))
  })
  .on(createVaultCategoryFx.doneData, (state, category) => {
    return [...state, category]
  })
  .on(updateVaultCategory, (state, category) =>
    state.map((c) => (c.id === category.id ? category : c)),
  )
  .on(getVaultCategoryFx.done, (state, { params: categoryId, result }) => {
    const category = categoryId ? (result as VaultCategory) : null

    return category
      ? state.map((c) => (c.id === category.id ? category : c))
      : state
  })
  .reset(refetchVaultCategories)
